import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, XCircle, Loader2 } from "lucide-react";
import { useSagaPolling } from "../hooks/useSagaPolling";
import { cancelSaga } from "../services/saga";
import { isTerminal } from "../types/saga";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/saga/StatusPill";
import { StepTimeline } from "../components/saga/StepTimeline";
import { JsonViewer } from "../components/saga/JsonViewer";
import { RequireRole } from "../components/RequireRole";

export default function SagaDetail() {
  const { id } = useParams<{ id: string }>();
  const [cancelling, setCancelling] = useState(false);
  const { saga, loading, error, refresh } = useSagaPolling(id ?? "");

  if (!id) {
    return (
      <div className="p-6">
        <p className="text-danger text-sm">ID inválido na rota.</p>
        <Link to="/sagas" className="inline-block mt-3">
          <Button size="sm" variant="outline"><ArrowLeft size={14} /> Sagas</Button>
        </Link>
      </div>
    );
  }

  const onCancel = async () => {
    // window.prompt segue o padrão estabelecido no app pra ações destrutivas
    // de saga (tier-change, delete-permanent). Retorna null se Esc/Cancel,
    // string vazia se confirmou sem reason, ou texto se digitou motivo.
    const reasonInput = window.prompt(
      "Cancelar esta saga? Opcionalmente, descreva o motivo (vai pro audit log).",
      "",
    );
    if (reasonInput === null) return;
    const reason = reasonInput.trim() || undefined;
    setCancelling(true);
    try {
      const result = await cancelSaga(id, reason);
      toast.success(`Saga cancelada — status ${result.status}`);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao cancelar");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            {saga?.sagaType ?? "Saga"}
            {saga && <StatusPill status={saga.status} />}
          </span>
        }
        subtitle={
          <code className="font-mono-tight text-xs text-muted-foreground">{id}</code>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/sagas"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Sagas</Button></Link>
            {saga && !isTerminal(saga.status) && (
              <RequireRole anyOf={["platform-master", "platform-admin"]}>
                <Button size="sm" variant="danger" disabled={cancelling} onClick={onCancel}>
                  {cancelling ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                  {cancelling ? "Cancelando" : "Cancelar"}
                </Button>
              </RequireRole>
            )}
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {loading && !saga && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {error && <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{error}</div>}

        {saga && (
          <>
            {/* Metadata grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Meta label="Tipo">{saga.sagaType}</Meta>
              <Meta label="Step atual">
                <span className="font-mono-tight">{saga.currentStep} / {saga.totalSteps}</span>
              </Meta>
              <Meta label="Iniciada">
                <span className="font-mono-tight text-xs">{new Date(saga.startedAt).toLocaleString("pt-BR")}</span>
              </Meta>
              <Meta label="Concluída">
                <span className="font-mono-tight text-xs">{saga.finishedAt ? new Date(saga.finishedAt).toLocaleString("pt-BR") : "—"}</span>
              </Meta>
              <Meta label="Idempotency Key" wide>
                <code className="font-mono-tight text-xs">{saga.idempotencyKey}</code>
              </Meta>
            </div>

            {saga.error && (
              <div className="rounded-md border border-danger/30 bg-danger-soft p-3 text-sm">
                <p className="font-medium text-danger-soft-fg mb-1">Erro na saga</p>
                <p className="text-danger-soft-fg/90">
                  Step <span className="font-mono-tight">#{saga.error.step}</span> · {saga.error.message}
                </p>
              </div>
            )}

            <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
              {/* Timeline */}
              <section>
                <h2 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider text-muted-foreground">
                  Steps
                </h2>
                <StepTimeline steps={saga.stepResults} />
              </section>

              {/* Right column: payload */}
              <section>
                <h2 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider text-muted-foreground">
                  Payload completo
                </h2>
                <JsonViewer value={saga} collapsible collapsedByDefault label="SagaRunResult" />
              </section>
            </div>

            {!isTerminal(saga.status) && (
              <p className="text-xs text-muted-foreground italic flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" />
                Polling a cada 2s enquanto status não for terminal.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Meta({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`rounded-md border border-border bg-surface p-3 ${wide ? "col-span-2 md:col-span-4" : ""}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className="text-sm mt-1">{children}</p>
    </div>
  );
}
